// Interstitial Form Submission Tracker
// Tracks form submissions per session and property to prevent repeated displays

import { getVWOFMEUserId } from './vwoApplyTest';

/**
 * Get consistent FME UUID for session tracking
 * @returns {string} FME user ID for consistent tracking
 */
const getSessionId = () => {
  // Use the same FME UUID system as event tracking and URL parameters
  return getVWOFMEUserId();
};

/**
 * Get the storage key for tracking form submissions
 * @param {string} sessionId - The session/user ID
 * @returns {string} Storage key
 */
const getStorageKey = (sessionId) => {
  return `interstitial_forms_${sessionId}`;
};

/**
 * Get submitted properties for current session
 * @returns {Array<string>} Array of property slugs where form was submitted
 */
export const getSubmittedProperties = () => {
  if (typeof window === 'undefined') return [];
  
  try {
    const sessionId = getSessionId();
    const storageKey = getStorageKey(sessionId);
    const stored = sessionStorage.getItem(storageKey);
    
    if (stored) {
      const data = JSON.parse(stored);
      // Verify the session ID matches (in case of session change)
      if (data.sessionId === sessionId) {
        return data.submittedProperties || [];
      }
    }
    
    return [];
  } catch (error) {
    console.error('🎯 InterstitialTracker: Error getting submitted properties:', error);
    return [];
  }
};

/**
 * Check if form was already submitted for a specific property in current session
 * @param {string} propertySlug - The property slug to check
 * @returns {boolean} True if form was already submitted for this property
 */
export const hasSubmittedForProperty = (propertySlug) => {
  if (!propertySlug) return false;
  
  const submittedProperties = getSubmittedProperties();
  return submittedProperties.includes(propertySlug);
};

/**
 * Mark form as submitted for a specific property
 * @param {string} propertySlug - The property slug where form was submitted
 */
export const markFormSubmitted = (propertySlug) => {
  if (typeof window === 'undefined' || !propertySlug) return;
  
  try {
    const sessionId = getSessionId();
    const storageKey = getStorageKey(sessionId);
    const submittedProperties = getSubmittedProperties();
    
    // Add property if not already in list
    if (!submittedProperties.includes(propertySlug)) {
      submittedProperties.push(propertySlug);
    }
    
    const data = {
      sessionId,
      submittedProperties,
      lastUpdated: new Date().toISOString()
    };
    
    sessionStorage.setItem(storageKey, JSON.stringify(data));
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 InterstitialTracker: Marked form submitted for property:', propertySlug);
      console.log('🎯 InterstitialTracker: All submitted properties:', submittedProperties);
      console.log('🎯 InterstitialTracker: Using FME UUID:', sessionId);
    }
    
  } catch (error) {
    console.error('🎯 InterstitialTracker: Error marking form submitted:', error);
  }
};

/**
 * Clear all tracking data (useful for testing or manual reset)
 */
export const clearTrackingData = () => {
  if (typeof window === 'undefined') return;
  
  try {
    const sessionId = getSessionId();
    const storageKey = getStorageKey(sessionId);
    sessionStorage.removeItem(storageKey);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🎯 InterstitialTracker: Cleared all tracking data for FME UUID:', sessionId);
    }
  } catch (error) {
    console.error('🎯 InterstitialTracker: Error clearing tracking data:', error);
  }
};

/**
 * Get debugging information about current tracking state
 * @returns {Object} Debug information
 */
export const getDebugInfo = () => {
  if (typeof window === 'undefined') {
    return { error: 'SSR - no window available' };
  }
  
  try {
    const sessionId = getSessionId();
    const submittedProperties = getSubmittedProperties();
    const storageKey = getStorageKey(sessionId);
    const rawData = sessionStorage.getItem(storageKey);
    
    return {
      sessionId,
      submittedProperties,
      storageKey,
      rawData: rawData ? JSON.parse(rawData) : null,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    return { error: error.message };
  }
};

// Make debug functions available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.interstitialDebug = {
    getSubmittedProperties,
    hasSubmittedForProperty,
    markFormSubmitted,
    clearTrackingData,
    getDebugInfo
  };
}